import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Play, ArrowLeft, Eye, Clock, Filter, Share2, Link, Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FreeLearningPage = () => {
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeVideo, setActiveVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const { videoId } = useParams();

  useEffect(() => { fetchVideos(); }, []);

  // Auto-select video from URL param
  useEffect(() => {
    if (videoId && videos.length > 0) {
      const v = videos.find(v => v.id === videoId);
      if (v) setActiveVideo(v);
    }
  }, [videoId, videos]);

  const fetchVideos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/free-videos/public`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = selectedCategory === 'all'
    ? videos
    : videos.filter(v => v.category === selectedCategory);

  const playVideo = (video) => {
    setActiveVideo(video);
    navigate(`/free-learning/${video.id}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getVideoLink = (video) => `${window.location.origin}/free-learning/${video.id}`;

  const copyLink = (video) => {
    navigator.clipboard.writeText(getVideoLink(video)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatDuration = (s) => {
    if (!s) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `0:${String(sec).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-golden border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="free-learning-page">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <h1 className="text-xl font-bold">
              Free <span className="text-golden">Learning</span>
            </h1>
          </div>
          <p className="text-gray-400 text-sm hidden md:block">Free educational content by Upper Circuit Stocks</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Active Video Player */}
        {activeVideo && (
          <div className="mb-8" data-testid="video-player-section">
            <div className="bg-black rounded-xl overflow-hidden mb-4 max-w-4xl mx-auto">
              <video
                ref={videoRef}
                src={`${API_URL}/api/free-videos/stream/${activeVideo.id}`}
                controls
                autoPlay
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                onContextMenu={(e) => e.preventDefault()}
                className="w-full aspect-video"
                data-testid="video-player"
              />
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold mb-1">{activeVideo.title}</h2>
                  {activeVideo.category && (
                    <Badge className="bg-golden/10 text-golden border-golden/20 text-xs mb-2">{activeVideo.category}</Badge>
                  )}
                  <p className="text-gray-400 text-sm">{activeVideo.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {activeVideo.view_count || 0} views</span>
                    {activeVideo.duration_seconds > 0 && (
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(activeVideo.duration_seconds)}</span>
                    )}
                  </div>
                </div>
                {/* Share Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => copyLink(activeVideo)}
                  className="flex-shrink-0 border-golden/30 text-golden hover:bg-golden/10"
                  data-testid="share-video-btn"
                >
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Share2 className="w-4 h-4 mr-1" />}
                  {copied ? 'Copied!' : 'Share'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="category-filter">
            <Filter className="w-4 h-4 text-gray-500" />
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedCategory === 'all' ? 'bg-golden text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All
            </button>
            {categories.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedCategory(c.name)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  selectedCategory === c.name ? 'bg-golden text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Video Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Play className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No free videos available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="video-grid">
            {filtered.map(video => (
              <Card
                key={video.id}
                className="bg-gray-900 border-gray-800 hover:border-golden/40 transition-all cursor-pointer group overflow-hidden"
                onClick={() => playVideo(video)}
                data-testid={`video-card-${video.id}`}
              >
                <CardContent className="p-0">
                  {/* Thumbnail - full image visible, no cropping */}
                  <div className="relative bg-gray-800 rounded-t-lg overflow-hidden">
                    {video.thumbnail_url ? (
                      <img
                        src={`${API_URL}${video.thumbnail_url}`}
                        alt={video.title}
                        className="w-full h-auto block"
                        style={{ minHeight: '160px', maxHeight: '260px', objectFit: 'contain', objectPosition: 'center', backgroundColor: '#1f2937' }}
                      />
                    ) : (
                      <div className="aspect-video flex items-center justify-center">
                        <Play className="w-10 h-10 text-gray-600" />
                      </div>
                    )}
                    {/* Hover play overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-golden/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-black ml-0.5" />
                      </div>
                    </div>
                    {video.duration_seconds > 0 && (
                      <span className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-1.5 py-0.5 rounded">
                        {formatDuration(video.duration_seconds)}
                      </span>
                    )}
                  </div>
                  {/* Caption below the thumbnail box */}
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-white line-clamp-2 leading-snug">{video.title}</h3>
                    {video.category && (
                      <p className="text-[11px] text-golden/70 mt-1">{video.category}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500">
                      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {video.view_count || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeLearningPage;
